import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import BoldText from '@components/admin/users/study-membership/trialos-study-membership/bold-text';
import { RISK_IQ_LABEL_KEYS } from '@components/admin/users/study-membership/trialos-study-membership/columns';
import {
    AccessMap,
    buildOriginalAccess,
    filterStudies,
    setAccess,
    sortStudiesNaturally,
    toMutationInput,
} from '@components/admin/users/study-membership/trialos-study-membership/helpers';
import StudyAccessTable from '@components/admin/users/study-membership/trialos-study-membership/study-access-table';
import Button from '@components/core/buttons';
import CoreConfirmationDialog from '@components/core/core-confirmation-dialog';
import { CustomDialog, DialogActions, DialogContent, DialogTitle } from '@components/core/dialog';
import TextField from '@components/core/text-field';
import { useDebounce, useTranslation } from 'hooks';
import '@components/core/filter.scss';
import { RiskIQAccess } from 'types/graphql-global-types';
import NotificationService from 'utils/notification';
import {
    OrganisationMemberFields,
    OrganisationAndStudyMemberFields_Organisation_studies_Study as OrganisationAndStudyMemberFields_studies,
} from '@queries/generated/fragments.generated';
import { GET_TRIALOS_STUDY_TEAM_MEMBERS_FOR_USER, SET_TRIALOS_STUDY_TEAM_MEMBERSHIP } from '@queries/studies';

export interface TrialOsStudyMembershipProps {
    open: boolean;
    user: OrganisationMemberFields | null;
    studies: (OrganisationAndStudyMemberFields_studies | null)[];
    canEdit?: boolean;
    // Without a RiskIQ license the organisation can't change RiskIQ access, so the dialog is read-only (ADO 91627)
    hasRiskIqLicense: boolean;
    onClose: (action?: 'success' | 'error') => void;
}

interface PendingBulk {
    level: RiskIQAccess;
    studyIds: string[];
}

// Not the legacy Filter: it remounts its auto-focused input whenever it re-renders (including its own
// debounce and typing), which steals focus from an open access dropdown and makes it re-open.
// Same markup and classes as Filter so it looks identical, but the input is mounted once.
const StudySearch = ({ onSearch }: { onSearch: (text: string) => void }) => {
    const { t } = useTranslation();
    const [text, setText] = useState('');
    const debouncedText = useDebounce(text, 300);

    useEffect(() => {
        onSearch(debouncedText.trim());
    }, [debouncedText, onSearch]);

    return (
        <div className="selections">
            <div className="text-criteria-component">
                <span className="k-textbox" style={{ width: '100%' }}>
                    <TextField
                        className="search-input"
                        id="trialos-study-membership-filter"
                        data-testid="trialos-study-membership-filter"
                        placeholder={t('enterSearchTerm')}
                        autoFocus
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        variant="outlined"
                        label={t('filterSearch')}
                    />
                </span>
            </div>
        </div>
    );
};

const TrialOsStudyMembership = ({
    open,
    user,
    studies,
    canEdit,
    hasRiskIqLicense,
    onClose,
}: TrialOsStudyMembershipProps) => {
    const { t } = useTranslation();
    const [edits, setEdits] = useState<AccessMap>(new Map());
    const [search, setSearch] = useState('');
    const [pendingBulk, setPendingBulk] = useState<PendingBulk | null>(null);
    const [confirmingClose, setConfirmingClose] = useState(false);
    const [saving, setSaving] = useState(false);

    const userId = user?.userId ?? null;
    const firstName = user?.user?.firstName ?? user?.firstName ?? '';
    const lastName = user?.user?.lastName ?? user?.lastName ?? '';
    const email = user?.user?.email ?? user?.email ?? '';
    const name = `${firstName} ${lastName}`.trim();

    const { data, loading, error } = useQuery(GET_TRIALOS_STUDY_TEAM_MEMBERS_FOR_USER, {
        variables: { userId },
        skip: !open || !userId,
        fetchPolicy: 'network-only',
    });
    // Without the current access we would show every study as No Access, so block editing instead
    const loadFailed = open && !!userId && !loading && (!!error || data?.trialOSStudyTeamMembersForUser == null);
    const readOnly = !canEdit || !hasRiskIqLicense || saving || loading || loadFailed;
    const [setMembership] = useMutation(SET_TRIALOS_STUDY_TEAM_MEMBERSHIP);

    const sortedStudies = useMemo(
        () =>
            sortStudiesNaturally(
                studies.flatMap((study) => (study ? [{ id: study.id, studyNumber: study.studyNumber ?? '' }] : [])),
            ),
        [studies],
    );
    const filteredStudies = useMemo(() => filterStudies(sortedStudies, search), [sortedStudies, search]);
    const original = useMemo(
        () =>
            buildOriginalAccess(
                sortedStudies,
                (data?.trialOSStudyTeamMembersForUser ?? []).flatMap((member) => (member ? [member] : [])),
            ),
        [sortedStudies, data],
    );

    // Never carry edits over to another user or another opening of the dialog
    useEffect(() => {
        setEdits(new Map());
        setSearch('');
        setPendingBulk(null);
        setConfirmingClose(false);
    }, [open, userId]);

    const requestClose = () => {
        if (edits.size > 0) setConfirmingClose(true);
        else onClose();
    };

    const save = async () => {
        setSaving(true);
        try {
            await setMembership({
                variables: { userId, studies: toMutationInput(edits), reason: '' },
                refetchQueries: [{ query: GET_TRIALOS_STUDY_TEAM_MEMBERS_FOR_USER, variables: { userId } }],
            });
            onClose('success');
        } catch {
            NotificationService.showNotification(t('errorMessage'));
        } finally {
            setSaving(false);
        }
    };

    const confirmKey = search.trim() ? 'confirmFiltered' : 'confirmAll';
    // React escapes the rendered text, so i18n must not HTML-escape names like O'Brien as well
    const confirmParams = {
        name,
        level: pendingBulk ? t(RISK_IQ_LABEL_KEYS[pendingBulk.level]) : '',
        count: pendingBulk?.studyIds.length ?? 0,
        search: search.trim(),
        interpolation: { escapeValue: false },
    };

    return (
        <>
            <CustomDialog
                onClose={requestClose}
                aria-labelledby="trialos-study-membership-modal-title"
                fullWidth
                scroll="paper"
                maxWidth="md"
                open={open}
            >
                <DialogTitle id="trialos-study-membership-modal-title">
                    {t('uam:trialOsStudyMembershipDialog.title')}
                    <FontAwesomeIcon
                        icon={['fal', 'times']}
                        className="fa-lg close-icon-cv"
                        onClick={requestClose}
                        data-testid="close-dialog"
                    />
                </DialogTitle>
                <DialogContent className="add-risk-dialog-content pb-0">
                    {user && (
                        <div className="content-text">
                            <span className="title">{t('user')}:</span>
                            {`${name} (${email})`}
                        </div>
                    )}
                    <div className="filter-item-mb">
                        <StudySearch onSearch={setSearch} />
                    </div>
                    <div className="list-info">
                        <p className="text-length" data-testid="record-count">
                            {filteredStudies.length} {t('uam:studyRecords')}
                        </p>
                    </div>
                    {!hasRiskIqLicense && (
                        <p data-testid="unlicensed-notice">{t('uam:trialOsStudyMembershipDialog.unlicensed')}</p>
                    )}
                    {loadFailed && (
                        <p className="error-text" data-testid="load-error">
                            {t('errorMessage')}
                        </p>
                    )}
                    <StudyAccessTable
                        studies={filteredStudies}
                        original={original}
                        edits={edits}
                        disabled={readOnly}
                        onChangeStudy={(studyId, level) => setEdits(setAccess(edits, original, [studyId], level))}
                        onChangeAll={(level) =>
                            setPendingBulk({ level, studyIds: filteredStudies.map((study) => study.id) })
                        }
                    />
                    <div className="trialos-membership-footer">
                        <span>
                            <FontAwesomeIcon icon={['fal', 'info-circle']} />{' '}
                            {t('uam:trialOsStudyMembershipDialog.legendReadOnly')}
                            {'    '}
                            {t('uam:trialOsStudyMembershipDialog.legendReadWrite')}
                        </span>
                        <span>
                            {filteredStudies.length}/{sortedStudies.length}
                        </span>
                    </div>
                </DialogContent>
                <DialogActions className="actions-add-risk assign-items flex-wrap pt-0">
                    <div className="selected-items-container mb-20">
                        <p>
                            <b data-testid="unsaved-count">{edits.size}</b>{' '}
                            {t('uam:trialOsStudyMembershipDialog.studiesWithUnsavedChanges')}
                        </p>
                    </div>
                    <Button
                        text="common:update"
                        id="trialos-membership-update"
                        disabled={edits.size === 0 || readOnly}
                        onClick={save}
                    />
                </DialogActions>
            </CustomDialog>
            <CoreConfirmationDialog
                open={!!pendingBulk}
                close={() => setPendingBulk(null)}
                onDecline={() => setPendingBulk(null)}
                onConfirm={() => {
                    if (pendingBulk) setEdits(setAccess(edits, original, pendingBulk.studyIds, pendingBulk.level));
                    setPendingBulk(null);
                }}
                title={t(`uam:trialOsStudyMembershipDialog.${confirmKey}.title`, confirmParams)}
                mainText={<BoldText text={t(`uam:trialOsStudyMembershipDialog.${confirmKey}.main`, confirmParams)} />}
                additionalText={
                    <>
                        {t(`uam:trialOsStudyMembershipDialog.${confirmKey}.additional`, confirmParams)}
                        <br />
                        <br />
                        <BoldText text={t('uam:trialOsStudyMembershipDialog.applyNote')} />
                    </>
                }
                declineBtnText={t('common:cancel')}
                submitBtnText={t(`uam:trialOsStudyMembershipDialog.${confirmKey}.submit`, confirmParams)}
            />
            <CoreConfirmationDialog
                open={confirmingClose}
                close={() => setConfirmingClose(false)}
                onDecline={() => setConfirmingClose(false)}
                onConfirm={() => {
                    setConfirmingClose(false);
                    onClose();
                }}
                title={t('common:unsavedData')}
                mainText={t('common:unsavedChanges')}
            />
        </>
    );
};

export default TrialOsStudyMembership;
